import { inject, injectable } from 'inversify';

import { ApiGateway, TYPES as ApiGatewayTypes, HttpResponse } from '@desygner/ui-core-api-gateway';

import type {
  CreateProjectPayload,
  ProjectPayload,
  UpdateProjectPayload,
} from '../models/project.model';

@injectable()
export class ProjectsService {
  constructor(@inject(ApiGatewayTypes.ApiGateway) private readonly apiGateway: ApiGateway) {}

  private buildProjectUrl(type: 'design' | 'template', id: string) {
    const projectType = type === 'template' ? 'templates' : 'designs';
    const companyData = this.apiGateway.company ? `companies/${this.apiGateway.company}` : '';

    const requestUrl = ['brand', companyData, projectType, id].filter(Boolean).join('/');

    return requestUrl;
  }

  public fetchProjectData = async (project: ProjectPayload): Promise<HttpResponse> => {
    const requestUrl = this.buildProjectUrl(project.type, String(project.id));

    return await this.apiGateway.fetch('GET', requestUrl);
  };

  public fetchPublicProjectData = async (project: ProjectPayload) => {
    const type = project.type === 'template' ? 'templates' : 'designs';

    const requestUrl = ['brand', type, project.id].filter(Boolean).join('/');

    return await this.apiGateway.fetch('GET', `${requestUrl}?public=true`, undefined, {
      requireCredentials: false,
    });
  };

  public fetchPage(projectId: string | number, pageId: string | number): Promise<HttpResponse> {
    const requestUrl = `business/projects/${projectId}/pages/${pageId}`;

    return this.apiGateway.fetch('GET', requestUrl);
  }

  public fetchPublicPage(projectId: string | number, pageId: string | number) {
    const requestUrl = `business/projects/${projectId}/pages/${pageId}?public=true`;

    return this.apiGateway.fetch('GET', requestUrl);
  }

  public fetchFontData(fontId: number): Promise<HttpResponse> {
    const requestUrl = `brand/companies/${this.apiGateway.company}/assets/fonts/${fontId}`;

    return this.apiGateway.fetch('GET', requestUrl);
  }

  public fetchPublicProjectFonts(projectId: string | number): Promise<HttpResponse> {
    const requestUrl = `business/projects/${projectId}/fonts`;

    return this.apiGateway.fetch('GET', requestUrl);
  }

  public createProject = async (payload: CreateProjectPayload): Promise<HttpResponse> => {
    const requestUrl = `brand/companies/${this.apiGateway.company}/designs`;

    return await this.apiGateway.fetch('POST', requestUrl, payload);
  };

  public updateProject = async (project: UpdateProjectPayload) => {
    const requestUrl = this.buildProjectUrl(project?.type ?? 'design', String(project.id));

    return await this.apiGateway.fetch('PUT', requestUrl, project);
  };
}
